'use client';

import { createContext, use, useEffect, useRef, useState, useSyncExternalStore } from 'react';

export enum PortalType {
  Modal = 'modal',
  Popup = 'popup',
}

export type UIContextValue = {
  registerUiPortal: (type: PortalType) => HTMLDivElement;
  removeUiPortal: (portalId: string) => void;
  featureFlags: string[];
  featureFlagsLoading: boolean;
};

export const UiContext = createContext<UIContextValue>({
  registerUiPortal: () => document.createElement('div'),
  removeUiPortal: () => null,
  featureFlags: [],
  featureFlagsLoading: true,
});

function getServerUiPortalSnapshot(): null {
  return null;
}

export function useUi() {
  return use(UiContext);
}

export function useUiPortal(portalType: PortalType) {
  const { registerUiPortal, removeUiPortal } = useUi();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dependenciesRef = useRef({ registerUiPortal, removeUiPortal, portalType });
  useEffect(() => {
    dependenciesRef.current = { registerUiPortal, removeUiPortal, portalType };
  }, [registerUiPortal, removeUiPortal, portalType]);
  // useSyncExternalStore requires a stable subscription identity. Build it once
  // while reading the latest dependencies through a ref, including in renderers
  // that do not run the React Compiler.
  const [subscribe] = useState(() => (onStoreChange: () => void) => {
    const dependencies = dependenciesRef.current;
    const container = dependencies.registerUiPortal(dependencies.portalType);
    containerRef.current = container;
    onStoreChange();
    return () => {
      containerRef.current = null;
      dependencies.removeUiPortal(container.id);
    };
  });
  const getSnapshot = () => containerRef.current;

  return useSyncExternalStore(subscribe, getSnapshot, getServerUiPortalSnapshot);
}
